import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Link } from 'src/components/Link';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { Identifier } from 'src/types/utilityTypes';

import { AssessmentFormValues } from '../AssessmentNewPage';

import AssessmentCustomTemplate from './AssessmentCustomTemplate';
import { UserAssessmentQuestion } from './UserAssessmentQuestion';

export const AssessmentPreview = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { values } = useFormikContext<AssessmentFormValues>();
  const { session } = useSession();

  const curDomain = externalLinks.railsBase || window.location.origin;

  const publicLink = values.id
    ? `${curDomain}/assessment/${values.id}`
    : t('saveToGenerateLink');

  const privateLink = values.id
    ? `${curDomain}/dashboard/me/assessment/take/${values.id}`
    : t('saveToGenerateLink');

  const buttonTextWithDefault = values.buttonText || t('submitAssessment');

  return (
    <>
      {!values.customTemplate && (
        <Typography variant="h4" sx={{ mb: 3 }}>
          {values.name || t('untitledAssessment')}
        </Typography>
      )}

      <Box component="form" sx={{ mb: 4 }}>
        {values.customTemplate ? (
          <AssessmentCustomTemplate
            assessment={{
              ...values,
              id: values.id as Identifier,
              userId: session.trainerId as Identifier,
              createdAt: new Date().getTime(),
              updatedAt: new Date().getTime(),
              subTrainerId: null,
            }}
          />
        ) : (
          <>
            <HtmlTypography>{values.description}</HtmlTypography>
            {values.fields.map((field, index) => (
              <Grid container key={index} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <UserAssessmentQuestion
                    field={field}
                    index={index}
                    fields={values.fields}
                  />
                </Grid>
              </Grid>
            ))}
          </>
        )}

        <Box sx={{ mt: 4 }}>
          <Button variant="contained" disabled fullWidth={false}>
            {buttonTextWithDefault}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('shareablePublicLink')}:
            </Typography>
            {values.id ? (
              <Link
                href={publicLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                {publicLink}
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {publicLink}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('shareablePrivateLink')}:
            </Typography>
            {values.id ? (
              <Link
                href={privateLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                {privateLink}
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {privateLink}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

